import "@/App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ScrollManager } from "@/components/ScrollManager";
import AnalyticsPageviews from "@/components/AnalyticsPageviews";
import { AuthProvider } from "@/context/AuthContext";
import WalletTopUpManager from "@/components/wallet/WalletTopUpManager";
import KundaliKhojEntry from "@/kundalikhoj/KundaliKhojEntry";
import HomeMobileCTABar from "@/components/HomeMobileCTABar";
import { ProtectedRoute } from "@/components/venture/ProtectedRoute";

/*
 * R109.25 — Route-level code splitting.
 *
 * Landing is the LCP-critical entry (Meta ads land here) so it stays eager.
 * Every other route is React.lazy'd — the browser only downloads the JS
 * chunk it actually needs, cutting main.js from ~1.97 MB → ~600 KB
 * uncompressed / 513 KB → ~160 KB gzipped on first paint.
 *
 * Third-party loaders (GA4, Meta Pixel, WhatsApp, FloatingWhatsApp) are
 * mounted via requestIdleCallback so they NEVER block the LCP paint.
 */
import Landing from "@/pages/Landing";                              // eager — LCP
import SeoMetaLoader from "@/components/venture/SeoMetaLoader";     // eager — SEO tags needed pre-paint

// Below-fold globals — deferred until after main paint via IdleMount.
const FloatingWhatsApp   = lazy(() => import("@/components/venture/FloatingWhatsApp"));
const GlobalSchemas      = lazy(() => import("@/components/venture/GlobalSchemas"));
const GA4Loader          = lazy(() => import("@/components/venture/GA4Loader"));
const MetaPixelLoader    = lazy(() => import("@/components/venture/MetaPixelLoader"));
// Toaster (Sonner) is passive on first paint — mount it after idle so the
// sonner + next-themes runtime never ships in main.js.
const Toaster            = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// All non-Landing routes — pure lazy.
const Login              = lazy(() => import("@/pages/Login"));
const AdminLogin         = lazy(() => import("@/pages/AdminLogin"));
const ProfileCompletion  = lazy(() => import("@/pages/ProfileCompletion"));
const ForgotPassword     = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword      = lazy(() => import("@/pages/ResetPassword"));
const Onboarding         = lazy(() => import("@/pages/Onboarding"));
const GuestMatchPreview  = lazy(() => import("@/pages/GuestMatchPreview"));
const Recommendations    = lazy(() => import("@/pages/Recommendations"));
const Compare            = lazy(() => import("@/pages/Compare"));
const SharedComparison   = lazy(() => import("@/pages/SharedComparison"));
const VentureDetail      = lazy(() => import("@/pages/VentureDetail"));
const FeasibilityReport  = lazy(() => import("@/pages/FeasibilityReport"));
import ReportErrorBoundary from "@/pages/ReportErrorBoundary";
const Paywall            = lazy(() => import("@/pages/Paywall"));
const Plans              = lazy(() => import("@/pages/Plans"));
const FranchiseRegister  = lazy(() => import("@/pages/FranchiseRegister"));
const FranchisePartner   = lazy(() => import("@/pages/FranchisePartner"));
const FranchisePublic    = lazy(() => import("@/pages/FranchisePublic"));
const FranchiseDashboard = lazy(() => import("@/pages/FranchiseDashboard"));
const PaymentSuccess     = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentFailure     = lazy(() => import("@/pages/PaymentFailure"));
const PaymentReturn      = lazy(() => import("@/pages/PaymentReturn"));
const KundaliKhoj        = lazy(() => import("@/kundalikhoj/KundaliKhoj"));const Dashboard          = lazy(() => import("@/pages/Dashboard"));
const KundaliSeoLanding  = lazy(() => import("@/pages/KundaliSeoLanding"));
const MyReports          = lazy(() => import("@/pages/MyReports"));
const Account            = lazy(() => import("@/pages/Account"));
const BusinessProfiles   = lazy(() => import("@/pages/BusinessProfiles"));
const ProfileCompare     = lazy(() => import("@/pages/ProfileCompare"));
const AdminPanel         = lazy(() => import("@/pages/AdminPanel"));
const PublicVenture      = lazy(() => import("@/pages/PublicVenture"));
const BusinessPartners   = lazy(() => import("@/pages/BusinessPartners"));
const PartnerDetail      = lazy(() => import("@/pages/PartnerDetail"));
const PartnerRegister    = lazy(() => import("@/pages/PartnerRegister"));
const PartnerPaymentReturn = lazy(() => import("@/pages/PartnerPaymentReturn"));
const BusinessIdeas      = lazy(() => import("@/pages/BusinessIdeas"));
const BusinessFinder     = lazy(() => import("@/pages/BusinessFinder"));
const BusinessIdeasOneSeg = lazy(() => import("@/pages/BusinessIdeasSlugRouter").then(m => ({ default: m.BusinessIdeasOneSeg })));
const BusinessIdeasTwoSeg = lazy(() => import("@/pages/BusinessIdeasSlugRouter").then(m => ({ default: m.BusinessIdeasTwoSeg })));
const Explore            = lazy(() => import("@/pages/Explore"));
const InvestmentBandLanding = lazy(() => import("@/pages/SeoLanding").then(m => ({ default: m.InvestmentBandLanding })));
const LocationLanding    = lazy(() => import("@/pages/LocationLanding"));
const LocationDirectory  = lazy(() => import("@/pages/LocationLanding").then(m => ({ default: m.LocationDirectory })));
const Blog               = lazy(() => import("@/pages/Blog").then(m => ({ default: m.Blog })));
const BlogPost           = lazy(() => import("@/pages/Blog").then(m => ({ default: m.BlogPost })));
const SeoAliasResolver   = lazy(() => import("@/pages/SeoAliasResolver"));
const About              = lazy(() => import("@/pages/About"));
const Contact            = lazy(() => import("@/pages/Contact"));
const WhyVentureKhoj     = lazy(() => import("@/pages/WhyVentureKhoj"));
const HowItWorks         = lazy(() => import("@/pages/HowItWorks"));
const FAQ                = lazy(() => import("@/pages/FAQ"));
const SampleReport       = lazy(() => import("@/pages/SampleReport"));
const SampleReportView   = lazy(() => import("@/pages/SampleReportView"));
const ReportsGuide       = lazy(() => import("@/pages/ReportsGuide"));
const Disclaimer         = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.Disclaimer })));
const PrivacyPolicy      = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.PrivacyPolicy })));
const Terms              = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.Terms })));
const RefundPolicy       = lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.RefundPolicy })));
const ConsentLog         = lazy(() => import("@/pages/ConsentLog"));

/**
 * IdleMount — renders `children` only after the browser is idle. Used to
 * defer non-critical globals (analytics, floating chat, schema markup)
 * so they never block LCP/TBT on the initial paint.
 */
function IdleMount({ children, timeout = 2000 }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ric =
      window.requestIdleCallback ||
      ((cb) => setTimeout(() => cb({ didTimeout: true, timeRemaining: () => 0 }), 800));
    const cancel =
      window.cancelIdleCallback || ((id) => clearTimeout(id));
    const id = ric(() => setReady(true), { timeout });
    return () => cancel(id);
  }, [timeout]);
  return ready ? <Suspense fallback={null}>{children}</Suspense> : null;
}

/**
 * Small skeleton shown while a lazy chunk is downloading. Kept intentionally
 * lightweight — a single colored bar — so it doesn't itself become a paint
 * cost. Applied at the Routes-level so route transitions never blank.
 */
function RouteFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="route-fallback"
      className="fixed inset-x-0 top-0 z-[70]"
    >
      {/* Top-of-page indeterminate progress bar — highly visible so users
          get instant feedback that a click landed while the next lazy
          chunk downloads. */}
      <div className="h-[3px] w-full bg-emerald-100 overflow-hidden">
        <div className="h-full w-1/3 bg-emerald-500 route-progress-bar" />
      </div>
      <span className="sr-only">Loading…</span>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            {/* PostHog SPA pageview tracker (no-op when PostHog isn't configured). */}
            <AnalyticsPageviews />

            {/* Scroll to top on route change; smooth-scroll to #hash if present.
                Also covers the lazy-chunk case by retrying until the target
                section mounts. */}
            <ScrollManager />

            {/* SeoMetaLoader is a null-render component that sets <title>/<meta>.
                Keep it eager so crawlers see the right tags immediately. */}
            <SeoMetaLoader />

            {/* R109.25 — 3rd-party loaders are behind IdleMount. They still
                run in ~1-2s but AFTER the LCP paint completes. */}
            <IdleMount><GA4Loader /></IdleMount>
            <IdleMount><MetaPixelLoader /></IdleMount>

            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/partner/login" element={<Login />} />
                <Route path="/franchisor/login" element={<Login />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/register" element={<Navigate to="/login" replace />} />
                <Route path="/complete-profile" element={<ProfileCompletion />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Public SEO pages */}
                <Route path="/business-ideas" element={<BusinessIdeas />} />
                <Route path="/business-finder" element={<BusinessFinder />} />
                <Route path="/business-ideas/location" element={<LocationDirectory />} />
                <Route path="/business-ideas/location/:city" element={<LocationLanding />} />
                <Route path="/business-ideas/budget/:band" element={<InvestmentBandLanding />} />
                <Route path="/business-ideas/:seg1" element={<BusinessIdeasOneSeg />} />
                <Route path="/business-ideas/:seg1/:seg2" element={<BusinessIdeasTwoSeg />} />
                <Route path="/explore" element={<Explore />} />
                <Route path="/venture/:slug" element={<PublicVenture />} />
                <Route path="/business-partners" element={<BusinessPartners />} />
                <Route path="/business-partners/brand/:slug" element={<PartnerDetail />} />
                <Route path="/business-partners/:categorySlug" element={<BusinessPartners />} />
                <Route path="/partner/register" element={<Navigate to="/franchise/register" replace />} />
                <Route path="/partner/payment-return" element={<PartnerPaymentReturn />} />
                <Route path="/become-a-business-partner" element={<Navigate to="/franchise/register" replace />} />

                {/* Trust / marketing */}
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/why-venturekhoj" element={<WhyVentureKhoj />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/sample-report" element={<SampleReport />} />
                <Route path="/sample-report/view" element={<ReportErrorBoundary><FeasibilityReport sample /></ReportErrorBoundary>} />
                <Route path="/reports" element={<Navigate to="/sample-report" replace />} />

                {/* Legal */}
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/account/consent-log" element={<ProtectedRoute><ConsentLog /></ProtectedRoute>} />

                {/* Pricing / Payment */}
                <Route path="/pricing" element={<Plans />} />
                <Route path="/plans" element={<Navigate to="/pricing" replace />} />
                <Route path="/franchise/register" element={<FranchiseRegister />} />
                <Route path="/franchise/partner" element={<FranchisePartner />} />
                <Route path="/brands/pricing"    element={<FranchisePartner />} />
                <Route path="/franchise/dashboard" element={<FranchiseDashboard />} />
                <Route path="/franchise/:slug" element={<FranchisePublic />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/failure" element={<PaymentFailure />} />
                <Route path="/payment/return" element={<PaymentReturn />} />

                {/* Public share pages */}
                <Route path="/shared/comparison/:id" element={<SharedComparison />} />

                {/* SEO redirects */}
                <Route path="/franchise-businesses" element={<Navigate to="/venture/franchise" replace />} />
                {/* R110.105 — email-campaign shortlinks that were 404'ing.
                    Redirect to the existing full pages so users landing from
                    the email get a working experience immediately. */}
                <Route path="/partner-details"  element={<Navigate to="/franchise/partner"  replace />} />
                <Route path="/list-your-brand"  element={<Navigate to="/franchise/register" replace />} />
                {/* R110.122 — Clean marketing alias for the B2B landing page. */}
                <Route path="/list-your-franchise" element={<Navigate to="/franchise/partner" replace />} />
                <Route path="/commercial-property" element={<Navigate to="/venture/commercial-property" replace />} />
                <Route path="/gym-business" element={<Navigate to="/venture/gym" replace />} />
                <Route path="/cafe-business" element={<Navigate to="/venture/cafe" replace />} />
                <Route path="/cloud-kitchen-business" element={<Navigate to="/venture/cloud-kitchen" replace />} />
                <Route path="/salon-business" element={<Navigate to="/venture/salon" replace />} />
                <Route path="/fitness-store-business" element={<Navigate to="/venture/fitness-equipment-store" replace />} />
                <Route path="/coaching-centre-business" element={<Navigate to="/venture/coaching-institute" replace />} />

                {/* SEO Phase 1 — Blog */}
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/category/:cat" element={<Blog />} />
                <Route path="/blog/tag/:tag" element={<Blog />} />
                <Route path="/blog/author/:slug" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />

                {/* Authenticated app */}
                {/* R110.40 — Public: guests take the questionnaire first, then auth. */}
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/kundali-khoj" element={<KundaliKhoj />} />
                <Route path="/kundali/:seg" element={<KundaliSeoLanding />} />
                <Route path="/matches-preview" element={<GuestMatchPreview />} />
                <Route path="/recommendations" element={<ProtectedRoute><Recommendations /></ProtectedRoute>} />
                <Route path="/compare" element={<ProtectedRoute><Compare /></ProtectedRoute>} />
                <Route path="/venture/:slug/feasibility" element={<ProtectedRoute><VentureDetail /></ProtectedRoute>} />
                <Route path="/report/:id" element={<ProtectedRoute><ReportErrorBoundary><FeasibilityReport /></ReportErrorBoundary></ProtectedRoute>} />
                <Route path="/paywall/:reportId" element={<ProtectedRoute><Paywall /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/my-reports" element={<ProtectedRoute><MyReports /></ProtectedRoute>} />
                <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                <Route path="/business-profiles" element={<ProtectedRoute><BusinessProfiles /></ProtectedRoute>} />
                <Route path="/business-profiles/compare" element={<ProtectedRoute><ProfileCompare /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>} />

                <Route path="*" element={<SeoAliasResolver />} />
              </Routes>
            </Suspense>

            <IdleMount><FloatingWhatsApp /></IdleMount>
            <WalletTopUpManager />
            <KundaliKhojEntry />
            <HomeMobileCTABar />
            <IdleMount><GlobalSchemas /></IdleMount>
            <IdleMount timeout={800}><Toaster position="top-center" richColors /></IdleMount>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
